/**
 * Remembers the community **Shop** page (`/shop`) between visits.
 *
 * Tapping a product opens `/shop/product/:id`; going back remounts the page,
 * which used to fetch page 1 again — throwing away every product already
 * loaded, drawing a **new random seed** (so the “Random” order changed) and
 * jumping back to the top. This module keeps one entry **per filter set**
 * (`shop|category|category_id|q|sort`): the loaded items, the page number, the
 * facet data (shops + categories), the random seed and the reading position.
 *
 * Kept in memory (survives SPA navigation = browser Back) and mirrored into
 * sessionStorage (survives a reload / F5 in the same tab; a new tab starts
 * fresh). Scroll tracking stays in memory only — writing the whole list on every
 * scroll event would be wasteful, so it is flushed when the tab is hidden.
 */
const KEY = 'ms_shop_state';
const MAX = 6;                       // remember the last 6 filter sets

let store = load();
let flush = null;

function load() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) || {} : {};
  } catch (e) {
    return {};
  }
}

function persist() {
  try { sessionStorage.setItem(KEY, JSON.stringify(store)); } catch (e) { /* full / private */ }
}

function flushSoon() {
  if (flush) return;
  flush = setTimeout(() => { flush = null; persist(); }, 400);
}

/** The state of one filter set — `null` the first time that set is opened. */
export const readShop = (key) => store[key] || null;

/**
 * Merge a patch into one filter set.
 * @param {string} key    the filter signature (`shop|category|category_id|q|sort`)
 * @param {object} patch
 * @param {boolean} [write] false = memory only (scroll position).
 */
export function saveShop(key, patch, write = true) {
  store[key] = { ...(store[key] || {}), ...patch, at: Date.now() };
  const keys = Object.keys(store);
  if (keys.length > MAX) {                       // keep only the newest few sets
    keys.sort((a, b) => (store[a].at || 0) - (store[b].at || 0));
    keys.slice(0, keys.length - MAX).forEach((k) => { delete store[k]; });
  }
  if (write) flushSoon();
  return store[key];
}

/** Forget one filter set (or every cached set when no key is given). */
export function forgetShop(key) {
  if (key) delete store[key];
  else store = {};
  persist();
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', persist);          // leaving / closing the tab
  document.addEventListener('visibilitychange', () => { if (document.hidden) persist(); });
}
